import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { useSite } from "@/site/SiteContext";
import { scrollToSection } from "@/lib/scroll";
import { mediaSrc } from "@/lib/media";
import { FavoriteButton } from "@/components/FavoriteButton";

const QuickViewContext = createContext({ products: [], open: () => {} });

export const useQuickView = () => useContext(QuickViewContext);

const badgeLabelFor = (t) => (b) => (b === "hit" ? t("catalog.badges.hit") : b === "new" ? t("catalog.badges.new") : "");
const badgeCls = (b) => (b === "hit" ? "bg-gold text-black" : "bg-emerald-500 text-white");

export const QuickViewProvider = ({ children }) => {
  const { t } = useLang();
  const { contact } = useSite();
  const [products, setProducts] = useState([]);
  const [quick, setQuick] = useState(null);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const open = useCallback((p) => setQuick(p), []);
  const close = useCallback(() => setQuick(null), []);

  useEffect(() => {
    if (!quick) return;
    const onKey = (e) => { if (e.key === "Escape") setQuick(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [quick]);

  const badgeLabel = badgeLabelFor(t);
  const orderUrl = (p) => `https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(`${t("catalog.quote")} — ${p.title}`)}`;

  const similar = useMemo(
    () => (quick ? products.filter((p) => p.category === quick.category && p.product_id !== quick.product_id).slice(0, 4) : []),
    [quick, products],
  );

  return (
    <QuickViewContext.Provider value={{ products, open }}>
      {children}

      <AnimatePresence>
        {quick && (
          <motion.div
            data-testid="catalog-quickview"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
            onClick={close}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl bg-card border border-gold/20 rounded-sm overflow-hidden max-h-[90vh] overflow-y-auto"
            >
              <button
                data-testid="quickview-close"
                onClick={close}
                className="absolute top-3 right-3 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-black/50 text-white hover:bg-gold hover:text-black transition-colors"
              >
                <X size={18} />
              </button>
              <div className="grid md:grid-cols-2">
                <div className="relative bg-muted h-64 md:h-80">
                  {quick.image ? <img src={mediaSrc(quick.image)} alt={quick.title} className="w-full h-full object-cover" /> : null}
                  <div className="absolute top-3 left-3 z-10">
                    <FavoriteButton product={quick} context="quickview" className="w-10 h-10" size={18} />
                  </div>
                </div>
                <div className="p-7 md:p-9 flex flex-col">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-widest text-gold">{t(`catalog.cats.${quick.category}`)}</span>
                    {quick.badge ? (
                      <span data-testid="quickview-badge" className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-sm ${badgeCls(quick.badge)}`}>
                        {badgeLabel(quick.badge)}
                      </span>
                    ) : null}
                  </div>
                  <h3 className="mt-2 font-serif text-2xl md:text-3xl text-foreground leading-tight">{quick.title}</h3>
                  {quick.description ? (
                    <p className="mt-4 text-sm text-muted-foreground font-sans leading-relaxed">{quick.description}</p>
                  ) : null}
                  <div className="mt-auto pt-6 flex flex-col gap-3">
                    <a
                      data-testid="quickview-whatsapp"
                      href={orderUrl(quick)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 bg-gold text-black font-sans font-semibold text-sm uppercase tracking-[0.12em] px-6 py-3.5 rounded-sm hover:bg-gold-deep transition-colors"
                    >
                      <MessageCircle size={17} /> {t("catalog.order")}
                    </a>
                    <button
                      data-testid="quickview-quote"
                      onClick={() => { close(); scrollToSection("contact"); }}
                      className="inline-flex items-center justify-center gap-2 border border-border text-foreground font-sans text-sm uppercase tracking-[0.12em] px-6 py-3.5 rounded-sm hover:border-gold hover:text-gold transition-colors"
                    >
                      {t("catalog.quote")} <ArrowUpRight size={15} />
                    </button>
                  </div>
                </div>
              </div>

              {similar.length > 0 && (
                <div className="border-t border-border p-6 md:p-8" data-testid="quickview-similar">
                  <h4 className="font-serif text-lg text-foreground">{t("catalog.similar")}</h4>
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {similar.map((s) => (
                      <button
                        key={s.product_id}
                        data-testid={`similar-${s.product_id}`}
                        onClick={() => setQuick(s)}
                        className="group text-left"
                      >
                        <div className="h-24 bg-muted rounded-sm overflow-hidden relative">
                          {s.badge ? (
                            <span className={`absolute top-1.5 left-1.5 z-10 text-[8px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${badgeCls(s.badge)}`}>
                              {badgeLabel(s.badge)}
                            </span>
                          ) : null}
                          {s.image ? (
                            <img src={mediaSrc(s.image)} alt={s.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          ) : null}
                        </div>
                        <span className="mt-1.5 block text-xs text-muted-foreground group-hover:text-gold transition-colors line-clamp-1">
                          {s.title}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </QuickViewContext.Provider>
  );
};
